// 웰로 상담창 SDK. 상담창 서버의 loader.js를 한 번만 불러오고 window.WelloChat을 타입 있게 쓴다.
// 상담창 자체는 loader.js가 그리므로, 상담창을 고쳐도 이 패키지를 쓰는 사이트는 다시 배포하지 않아도 된다

export type WelloChatBootOptions = {
  // 로그인 회원이면 사이트 서버에서 받은 위젯 토큰. 비우면 비회원으로 상담한다
  memberToken?: string | null;
  // 기본 런처 버튼을 숨기고 사이트의 버튼에서 open()을 부를 때
  hideLauncher?: boolean;
};

export type WelloChatEvent = 'unread' | 'open' | 'close';

export type WelloChatApi = {
  version: string;
  boot: (options?: WelloChatBootOptions) => void;
  shutdown: () => void;
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
  // 등록 해제 함수를 돌려준다. unread는 등록 즉시 현재 미읽음 수를 한 번 알린다
  on: (event: WelloChatEvent, listener: (count?: number) => void) => () => void;
};

type ChatWindow = Window & { WelloChat?: unknown };

const loadedApi = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const chat = (window as ChatWindow).WelloChat;

  return chat && typeof chat === 'object' && 'version' in chat ? (chat as WelloChatApi) : null;
};

let loading: Promise<WelloChatApi> | null = null;
let markReady: (chat: WelloChatApi) => void = () => undefined;
const ready = new Promise<WelloChatApi>((resolve) => {
  markReady = resolve;
});

// src: 상담창 서버의 loader.js 주소(환경별로 다르다, README 참고)
export const loadWelloChat = (src: string) => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('[WelloChat] 브라우저에서만 불러올 수 있습니다'));
  }
  const current = loadedApi();
  if (current) {
    markReady(current);

    return Promise.resolve(current);
  }
  if (loading) {
    return loading;
  }
  loading = new Promise<WelloChatApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      const chat = loadedApi();
      if (chat) {
        // <WelloChat>의 boot가 먼저 돌고, 기다리던 open() 등이 뒤따르게 한다
        resolve(chat);
        markReady(chat);
      } else {
        loading = null;
        reject(new Error(`[WelloChat] 로더가 WelloChat을 만들지 않았습니다: ${src}`));
      }
    };
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error(`[WelloChat] 로더를 불러오지 못했습니다: ${src}`));
    };
    document.head.appendChild(script);
  });

  return loading;
};

// 누군가 loadWelloChat을 부르거나 스니펫으로 로더가 뜰 때까지 기다린다(사이트 버튼에서 open() 등)
export const whenWelloChatReady = () => {
  const current = loadedApi();

  return current ? Promise.resolve(current) : ready;
};
